import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { useEffect, useState, type ReactNode } from 'react';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LeaveRequestSchema,
  REQUEST_STATUSES,
  type EmployeeDto,
  type LeaveRequestDto,
} from '@taqon/contracts';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router';
import { ApiError, getData } from '../../shared/api/client';
import { RecordHistory } from '../../shared/audit/AuditTrail';
import { DocumentsPanel, useRecordDocuments } from '../../shared/documents/DocumentsPanel';
import { hasCapability, useSession } from '../../shared/session/session';
import { StatusChip } from '../../shared/ui/forms';
import { ExportButton } from '../../shared/export/ExportButton';
import { exportHref } from '../../shared/export/export-href';
import { filterQuery } from '../../shared/export/filter-query';
import { ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { ApprovalPanel } from '../../shared/workflow/ApprovalPanel';
import { Facts, RecordFailure, RecordLink } from '../finance/detail';
import { EmployeePicker, MasterSelect, useActiveMasters } from '../finance/lookups';
import { UrlDimensionNote } from '../reporting/dimensions';
import {
  BalancesTable,
  CancelLeaveDialog,
  CreatedNotice,
  daysLabel,
  durationLabel,
  LEAVE_REQUESTS,
  LeaveRequestDialog,
  LeaveRequestList,
  leaveRequestPath,
  NoEmployeeLink,
  useBalances,
  useRequestableTypes,
  supportingRequired,
} from './leave';
import { LeaveCancellationsSection, useLeaveCancellations } from './LeaveCancellation';
import { useFilterEmployee, useUrlFilters } from './url-filters';
import { useActionFeedback } from '../happiness/ActionFeedback';
import { SearchFilterBar } from '../../shared/ui/filters';
import { PersonName } from '../../shared/ui/person';

/**
 * My leave (`/hr/leave`; FRD-HRP-027, HRP-028, FRD-USR-023): the signed-in employee's balances
 * per leave type with the period each is for, their requests, a request dialog and cancelling a
 * request still in approval. Everything is the caller's own, through self-service.
 */
export function MyLeavePage() {
  const session = useSession();
  const navigate = useNavigate();
  const canCreate = hasCapability(session.data, 'hr.leave_request:create');
  const balances = useBalances(null);
  const [requesting, setRequesting] = useState(false);
  const unlinked = balances.error instanceof ApiError && balances.error.status === 404;

  return (
    <Stack spacing={3}>
      <PageHeader
        title="My leave"
        description="Your leave balances and requests."
        actions={
          canCreate && !unlinked ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={(event) => {
                event.currentTarget.focus();
                setRequesting(true);
              }}
            >
              Request leave
            </Button>
          ) : undefined
        }
      />
      {unlinked ? (
        <NoEmployeeLink />
      ) : (
        <>
          {balances.isError && <ErrorState message={balances.error.message} />}
          <Section title="Balances">
            {balances.isPending && <LoadingState label="Loading your balances" />}
            {balances.data !== undefined && (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ px: 2, pt: 1.5 }}>
                  As of {balances.data.asOf}. Remaining = entitlement + carried forward − taken;
                  available = remaining − pending (requests still in approval).
                </Typography>
                <BalancesTable
                  balances={balances.data.balances}
                  shortLeave={balances.data.shortLeave}
                />
              </>
            )}
          </Section>
          <Section title="My requests">
            <LeaveRequestList
              filters={{ mine: 'true' }}
              showEmployee={false}
              cancellable
              emptyTitle="You have no leave requests"
              label="your leave requests"
            />
          </Section>
        </>
      )}
      {requesting && (
        <LeaveRequestDialog
          employee={null}
          onClose={(saved) => {
            setRequesting(false);
            if (saved !== undefined) void navigate(`${leaveRequestPath(saved.id)}?created=1`);
          }}
        />
      )}
    </Stack>
  );
}

const LEAVE_FILTER_KEYS = [
  'employee',
  // A dashboard's employee dimension (FRD-RPT-010); the page's own picker is `employee`.
  'employee_id',
  'department_id',
  'type',
  'status',
  'from',
  'to',
  'start_from',
  'start_to',
] as const;

/**
 * Leave requests (`/hr/leave/requests`; FRD-HRP-031, FRD-RPT-022): every request the session may
 * see, filtered on the server by employee, department, leave type, status and a date range the
 * leave overlaps. HR may submit on an employee's behalf within its scope.
 */
export function LeaveRequestsPage() {
  const session = useSession();
  const navigate = useNavigate();
  const canCreate = hasCapability(session.data, 'hr.leave_request:create');
  const canPickEmployees = hasCapability(session.data, 'hr.employee:view');
  const canSeeDepartments = hasCapability(session.data, 'organization.department:view');
  const departments = useActiveMasters('/api/v1/organization/departments', canSeeDepartments);
  const types = useRequestableTypes();
  // Every filter lives in the URL, so a filtered list survives a refresh and a dashboard figure
  // opens the list behind it (FRD-RPT-007, RPT-022).
  const [url, setUrl] = useUrlFilters(LEAVE_FILTER_KEYS);
  const employee = useFilterEmployee(url.employee, canPickEmployees);
  const [creating, setCreating] = useState(false);
  const setEmployee = (value: EmployeeDto | null) => {
    setUrl('employee', value?.id ?? '');
  };
  const setDepartmentId = (value: string) => {
    setUrl('department_id', value);
  };
  const { type: typeId, status, from, to, start_from: startFrom, start_to: startTo } = url;
  const setTypeId = (value: string) => {
    setUrl('type', value);
  };
  const setStatus = (value: string) => {
    setUrl('status', value);
  };
  const setFrom = (value: string) => {
    setUrl('from', value);
  };
  const setTo = (value: string) => {
    setUrl('to', value);
  };
  const setStartFrom = (value: string) => {
    setUrl('start_from', value);
  };
  const setStartTo = (value: string) => {
    setUrl('start_to', value);
  };
  const departmentId = url.department_id;

  const filters = {
    employee_id: url.employee === '' ? url.employee_id : url.employee,
    department_id: departmentId,
    leave_type_id: typeId,
    status,
    from,
    to,
    start_from: startFrom,
    start_to: startTo,
  };

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Leave requests"
        description="Leave across the employees you may see, for approvals and reporting."
        actions={
          <>
            <ExportButton
              recordType="hr.leave_request"
              href={exportHref(LEAVE_REQUESTS, filterQuery(filters, '-start_date'))}
            />
            {canCreate && canPickEmployees && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={(event) => {
                  event.currentTarget.focus();
                  setCreating(true);
                }}
              >
                New leave request
              </Button>
            )}
          </>
        }
      />
      <UrlDimensionNote keys={['employee_id']} />
      <Box role="search" aria-label="Filter leave requests">
        <SearchFilterBar>
          {canPickEmployees && (
            <Stack sx={{ minWidth: 240 }}>
              <EmployeePicker value={employee} onChange={setEmployee} />
            </Stack>
          )}
          {canSeeDepartments && (
            <MasterSelect
              label="Department"
              value={departmentId}
              onChange={setDepartmentId}
              options={departments.data?.items ?? []}
            />
          )}
          <TextField
            select
            size="small"
            label="Leave type"
            value={typeId}
            onChange={(e) => {
              setTypeId(e.target.value);
            }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">Any</MenuItem>
            {(types.data ?? []).map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
            }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">Any</MenuItem>
            {REQUEST_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            type="date"
            label="On leave from"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            size="small"
            type="date"
            label="On leave to"
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            size="small"
            type="date"
            label="Starting from"
            value={startFrom}
            onChange={(e) => {
              setStartFrom(e.target.value);
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            size="small"
            type="date"
            label="Starting to"
            value={startTo}
            onChange={(e) => {
              setStartTo(e.target.value);
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </SearchFilterBar>
      </Box>
      <Section>
        <LeaveRequestList
          key={JSON.stringify(filters)}
          filters={filters}
          showEmployee
          cancellable={false}
          emptyTitle="No leave requests found"
          label="leave requests"
        />
      </Section>
      {creating && (
        <LeaveRequestDialog
          employee={undefined}
          onClose={(saved) => {
            setCreating(false);
            if (saved !== undefined) void navigate(`${leaveRequestPath(saved.id)}?created=1`);
          }}
        />
      )}
    </Stack>
  );
}

/**
 * One leave request (`/hr/leave/requests/:id`): its facts with the employee's balance for the
 * type at a glance, the approval history with the approver's decisions, supporting documents, its
 * audit history, and the requester's actions — cancel while in approval, edit and resubmit once
 * returned or rejected.
 */
export function LeaveRequestDetailPage() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const session = useSession();
  const detail = useQuery({
    queryKey: ['hr', 'leave', 'request', id],
    queryFn: () => getData(`${LEAVE_REQUESTS}/${id}`, LeaveRequestSchema),
  });
  // Back to where the request most likely came from: one's own leave, or the leave list.
  const own = detail.data !== undefined && detail.data.requestedBy === session.data?.userId;
  const back = (
    <Button
      component={RouterLink}
      to={own ? '/hr/leave' : '/hr/leave/requests'}
      startIcon={<ArrowBackIcon />}
    >
      {own ? 'My leave' : 'Leave requests'}
    </Button>
  );
  if (detail.isPending) return <LoadingState label="Loading the leave request" />;
  if (detail.isError) {
    return (
      <RecordFailure title="Leave request" noun="leave request" error={detail.error} back={back} />
    );
  }
  return (
    <LeaveRequestRecord request={detail.data} back={back} created={params.get('created') === '1'} />
  );
}

function LeaveRequestRecord({
  request,
  back,
  created,
}: {
  request: LeaveRequestDto;
  back: ReactNode;
  created: boolean;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const cancellations = useLeaveCancellations(request.id);
  // Just submitted: offer to rate how it went (FRD-CORE-062), once a day.
  const feedback = useActionFeedback();
  const { offer } = feedback;
  useEffect(() => {
    if (created) offer('leave_request');
  }, [created, offer]);
  const isRequester = session.data?.userId === request.requestedBy;
  const canChange = hasCapability(session.data, 'hr.leave_request:change');
  const canSeeEmployees = hasCapability(session.data, 'hr.employee:view');
  const [cancelling, setCancelling] = useState(false);
  const [editing, setEditing] = useState(false);
  const balances = useBalances(request.employeeId, { asOf: request.startDate });
  const balance = balances.data?.balances.find((b) => b.leaveTypeId === request.leaveTypeId);
  const open = request.status === 'submitted' || request.status === 'returned';
  // CLR-46 (FRD-HRP-094): approval is refused until a supporting document has been scanned.
  const evidenceRequired = supportingRequired(
    request.supportingRequiredAboveDays,
    Number(request.durationDays),
  );
  const documents = useRecordDocuments('hr.leave_request', request.id, evidenceRequired);
  const evidenceMissing =
    evidenceRequired &&
    open &&
    documents.data !== undefined &&
    !documents.data.some((d) => d.status === 'available');

  return (
    <Stack spacing={3}>
      <PageHeader
        title={`Leave request ${request.code}`}
        description={`${request.employeeName} · ${request.leaveTypeName}`}
        actions={
          <>
            {back}
            {request.status === 'submitted' && (isRequester || canChange) && (
              <Button
                variant="outlined"
                color="warning"
                onClick={(event) => {
                  event.currentTarget.focus();
                  setCancelling(true);
                }}
              >
                Withdraw
              </Button>
            )}
            {(request.status === 'returned' || request.status === 'rejected') && isRequester && (
              <Button
                variant="contained"
                onClick={(event) => {
                  event.currentTarget.focus();
                  setEditing(true);
                }}
              >
                Edit and resubmit
              </Button>
            )}
          </>
        }
      />
      {created && <CreatedNotice code={request.code} />}
      {feedback.dialog}
      {evidenceMissing && (
        <Alert severity="warning" variant="outlined">
          {documents.data.some((d) => d.status === 'pending_scan')
            ? 'The supporting document is being scanned. The request can be approved once the scan is clean.'
            : 'This leave needs a supporting document before it can be approved. Upload it under Supporting documents below.'}
        </Alert>
      )}
      <Section title="Request">
        <Facts
          label="Leave request details"
          facts={[
            ['Status', <StatusChip key="s" status={request.status} />],
            ['Number', request.code],
            [
              'Employee',
              <PersonName key="e" name={request.employeeName} employeeId={request.employeeId}>
                {canSeeEmployees ? (
                  <RecordLink to={`/hr/employees/${request.employeeId}`}>
                    {request.employeeCode} — {request.employeeName}
                  </RecordLink>
                ) : (
                  `${request.employeeCode} — ${request.employeeName}`
                )}
              </PersonName>,
            ],
            ['Leave type', request.leaveTypeName],
            ['From', request.startDate],
            ['To', request.endDate],
            ['Duration', durationLabel(request)],
            ...(request.convertedFromLeaveTypeName === null
              ? []
              : [
                  [
                    'Requested as',
                    `${request.convertedFromLeaveTypeName}, ${daysLabel(request.durationHours ?? '')} hours (above its hours per request)`,
                  ] satisfies [string, string],
                ]),
            ['Reason', request.reason ?? '—'],
            [
              'Delegate',
              request.delegateName === null ? (
                '—'
              ) : (
                <PersonName
                  key="d"
                  name={request.delegateName}
                  employeeId={request.delegateEmployeeId}
                >
                  {request.delegateName}, covering the work during the leave
                </PersonName>
              ),
            ],
            ['Submitted', new Date(request.submittedAt).toLocaleString()],
            [
              // Withdrawn in approval, the time is the withdrawal. Cancelled after approval (an
              // approved cancellation), the leave keeps its approval date.
              request.status === 'cancelled' &&
              cancellations.data !== undefined &&
              !cancellations.data.some((c) => c.status === 'approved')
                ? 'Withdrawn'
                : 'Decided',
              request.decidedAt === null ? '—' : new Date(request.decidedAt).toLocaleString(),
            ],
            [
              'Balance',
              balance === undefined
                ? balances.isPending
                  ? '…'
                  : 'No balance is tracked for this type'
                : `${daysLabel(balance.remaining)} remaining, ${daysLabel(balance.pending)} pending, ${daysLabel(balance.available)} available in ${balance.periodStart ?? ''} to ${balance.periodEnd ?? ''}`,
            ],
          ]}
        />
      </Section>
      {/* Cancelling approved leave through its own approval (FRD-HRP-030). */}
      {(request.status === 'approved' || request.status === 'cancelled') && (
        <LeaveCancellationsSection request={request} />
      )}
      <ApprovalPanel
        recordType="hr.leave_request"
        recordId={request.id}
        onDecided={() => {
          void queryClient.invalidateQueries({ queryKey: ['hr', 'leave'] });
        }}
      />
      {/* Supporting documents (FRD-HRP-027) while the request is open to its requester. */}
      <DocumentsPanel
        recordType="hr.leave_request"
        recordId={request.id}
        canAttach={open && (isRequester || canChange)}
        title="Supporting documents"
      />
      <RecordHistory recordType="hr.leave_request" recordId={request.id} />
      {cancelling && (
        <CancelLeaveDialog
          request={request}
          onClose={() => {
            setCancelling(false);
          }}
        />
      )}
      {editing && (
        <LeaveRequestDialog
          employee={null}
          editing={request}
          onClose={() => {
            setEditing(false);
          }}
        />
      )}
      {balances.isError &&
        !(balances.error instanceof ApiError && balances.error.status === 404) && (
          <ErrorState message={balances.error.message} />
        )}
    </Stack>
  );
}
